/* AddImagesComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./AddImagesRenderer");
const FileUploadUtility = require("../addinstructions/utils/FileUploadUtility");

/**
 * Component for adding images to IoT targets (Platforms, OS Plans, or Upgrade Plans).
 * Supports two modes:
 * 1. Editor mode: Single target entity passed directly
 * 2. Explorer mode: Array of target IDs, entities loaded from backend
 */
class AddImagesComponent extends EventEmitter {
    constructor(
        logger,
        selectedTargets,
        odmOsLinks,
        OdmOsLink,
        targetEntity,
        activeOrgId,
        userSession,
        isEditorMode
    ) {
        super();
        this._logger = logger;
        this._selectedTargets = selectedTargets;
        this._odmOsLinks = odmOsLinks;
        this._activeOrgId = activeOrgId;
        this._userSession = userSession;
        this._OdmOsLink = OdmOsLink;
        this._targetEntity = targetEntity;
        this._isEditorMode = isEditorMode;
        this._fileUploader = null;
        this._currentImageId = null;
        this._currentImage = null;
        this._summaryValues = {
            totalImages: 0,
            totalTargets: 0,
            total: 0,
            successCount: 0,
            failureCount: 0,
            skippedCount: 0,
            errors: [],
        };
        this._osLinkOptions = this._generateOsLinkOptions();
        this._renderer = new Renderer(this, selectedTargets, isEditorMode);
        this._renderer.render();
    }

    get renderer() {
        return this._renderer;
    }

    get logger() {
        return this._logger;
    }

    get selectedTargets() {
        return this._selectedTargets;
    }

    get isEditorMode() {
        return this._isEditorMode;
    }

    shutdown() {
        if (this._fileUploader) {
            this._fileUploader.destroy();
            this._fileUploader = null;
        }

        return this._renderer.shutdown().then(
            () => {
                this._logger = undefined;
                this._selectedTargets = undefined;
                this._odmOsLinks = undefined;
                this._osLinkOptions = undefined;
                this._renderer = undefined;
                this._OdmOsLink = undefined;
                this._targetEntity = undefined;
                this._activeOrgId = undefined;
                this._isEditorMode = undefined;
                this._fileUploader = undefined;
                this._currentImageId = undefined;
                this._currentImage = undefined;
                this._summaryValues = undefined;
                this._context = undefined;
                return Promise.resolve();
            },
            (err) => {
                this._logger.warn({error: err}, "AddImagesComponent.shutdown() error");
                return Promise.reject();
            }
        );
    }

    closeAction() {
        this._context.reject({userRejected: true});
    }

    /**
     * Creates a new image and links it to selected targets.
     */
    createImage(imageData) {
        const newOdmOsLink = new this._OdmOsLink(this._activeOrgId);
        newOdmOsLink.name = imageData.name;
        newOdmOsLink.description = imageData.description;
        newOdmOsLink.linkStatus = imageData.linkStatus;
        newOdmOsLink.url = imageData.url;
        newOdmOsLink.manual = true;

        newOdmOsLink
            .create()
            .then(async (createdLink) => {
                // Link the new image to all selected targets
                try {
                    await this._linkImageToTargets([createdLink.id]);
                    this._context.resolve({success: true, mode: "create", imageData});
                    return Promise.resolve();
                } catch (err) {
                    return Promise.reject(err);
                }
            })
            .catch((err) => {
                // Error - display inline and keep veil open
                this._logger.warn({error: err}, "AddImagesComponent.createImage() error");
                let errorMsg = "";
                if (err.errorCode === "EntityAlreadyExists") {
                    errorMsg = "This Image URL already exists";
                } else {
                    errorMsg = err.errorMsg || err.message || "Failed to create image";
                }
                this.emit("ShowError", errorMsg);
            });
    }

    /**
     * Uploads an image file and links it to selected targets.
     * Creates entity first, then uploads file with entity ID in metadata.
     */
    uploadImageFile(imageData, file) {
        // Create new image entity first
        this._createNewImageEntity(imageData.name, imageData.description, file.name)
            .then((image) => {
                this._currentImage = image;
                this._currentImageId = image.id;
                const username = this._userSession?.user?.userName || "unknown";
                const metadata = {
                    username: username,
                    filename: file.name,
                    filetype: "binary",
                    destination: "odm",
                    organizationId: this._activeOrgId,
                    linkId: image.id,
                };
                this._fileUploader = new FileUploadUtility({
                    file: file,
                    metadata: metadata,
                    endpoint: "/gfs/uploads/",
                    logger: this._logger,
                });

                this._fileUploader.on("UploadProgress", (bytesUploaded, bytesTotal, percentage) => {
                    const progress = this._fileUploader.getProgress();
                    this._currentImage.setProgress(progress);
                    if (this._currentImage.isDirty()) {
                        this._currentImage
                            .update({
                                skipUpdatedOn: true,
                                skipAudit: true,
                            })
                            .catch((err) => {
                                this._logger.warn(
                                    {error: err},
                                    `Failed to update odmOsLink ${this._currentImage.name}`
                                );
                            });
                    }
                    this.emit("UploadProgress", bytesUploaded, bytesTotal, percentage);
                });

                this._fileUploader.on("UploadSuccess", (result) => {
                    const progress = this._fileUploader.getProgress();
                    this._currentImage.setProgress(progress);
                    if (this._currentImage.isDirty()) {
                        this._currentImage
                            .update({
                                skipUpdatedOn: true,
                                skipAudit: true,
                            })
                            .catch((err) => {
                                this._logger.warn(
                                    {error: err},
                                    `Failed to update odmOsLink ${this._currentImage.name}`
                                );
                            });
                    }
                    this._linkImageToTargets([this._currentImageId])
                        .then(() => {
                            this._context.resolve({
                                success: true,
                                mode: "upload",
                                filename: file.name,
                                imageId: this._currentImageId,
                            });
                        })
                        .catch((err) => {
                            this._logger.warn(
                                {error: err},
                                "Failed to link image to targets after upload"
                            );
                            const errorMsg =
                                err.errorMsg || err.message || "Failed to link image to targets";
                            this.emit("ShowError", errorMsg);
                        });
                });

                this._fileUploader.on("UploadError", (error) => {
                    this._logger.warn({error}, "File upload failed");
                    // Delete the created entity if upload fails
                    if (this._currentImage) {
                        this._currentImage
                            .delete()
                            .then(() => {
                                this._currentImage = null;
                                this._currentImageId = null;
                            })
                            .catch((err) => {
                                this._logger.warn(
                                    {error: err},
                                    "Failed to delete image after upload error"
                                );
                            });
                    }
                    const errorMsg = error.message || "Failed to upload image file";
                    this.emit("ShowError", errorMsg);
                    this.emit("UploadReset");
                });

                this._fileUploader.on("UploadAborted", (result) => {
                    this._logger.info("Upload aborted");
                    if (this._currentImage) {
                        this._currentImage
                            .delete()
                            .then(() => {
                                this._currentImage = null;
                                this._currentImageId = null;
                            })
                            .catch((err) => {
                                this._logger.warn(
                                    {error: err},
                                    "Failed to delete image after abort"
                                );
                            });
                    }
                    this.emit("ShowError", "Upload was cancelled");
                    this.emit("UploadReset");
                });
                return this._fileUploader.start();
            })
            .catch((err) => {
                this._logger.warn({error: err}, "Failed to create image entity");
                const errorMsg = err.errorMsg || err.message || "Failed to create image";
                this.emit("ShowError", errorMsg);
                this.emit("UploadReset");
            });
    }

    abortUpload() {
        if (this._fileUploader && this._fileUploader.isUploading()) {
            this._logger.info("Aborting file upload");
            this._fileUploader.abort();
        }
    }

    resetUploadState() {
        if (this._fileUploader) {
            this._fileUploader.destroy();
            this._fileUploader = null;
        }
        this._currentImageId = null;
        this._currentImage = null;
    }

    /**
     * Links existing images to selected targets.
     */
    async linkExistingImages(imageIds) {
        this._summaryValues.totalImages = imageIds.length;
        this._summaryValues.totalTargets = Array.isArray(this._selectedTargets)
            ? this._selectedTargets.length
            : 1;

        await this._linkImageToTargets(imageIds);
        const failedLinks = this._summaryValues.failureCount;
        const skippedLinks = this._summaryValues.skippedCount;
        if (failedLinks > 0 || skippedLinks > 0) {
            this._context.resolve({
                success: false,
                mode: "link",
                imageIds,
                totalImages: this._summaryValues.totalImages,
                totalTargets: this._summaryValues.totalTargets,
                total: this._summaryValues.total,
                successCount: this._summaryValues.successCount,
                failureCount: this._summaryValues.failureCount,
                skippedCount: this._summaryValues.skippedCount,
                errors: this._summaryValues.errors,
            });
        } else {
            this._context.resolve({success: true, mode: "link", imageIds});
        }
    }

    /**
     * Gets OS link options for combobox.
     */
    getOsLinkOptions() {
        return this._osLinkOptions;
    }

    // prettier-ignore
    set "$teve.ui.context"(context) { //jshint ignore:line
        this._context = context;
    }

    /**
     * Links images to a single target entity sequentially.
     * @private
     */
    _linkImagesToSingleTarget(target, osLinkIds, existingImageIds) {
        // Process images sequentially - wait for each updateRelationships to complete
        return osLinkIds.reduce((promiseChain, imageId) => {
            return promiseChain.then((results) => {
                if (existingImageIds.indexOf(imageId) >= 0) {
                    results.push({
                        status: "fulfilled",
                        value: {success: 0, skipped: 1, failed: 0, errors: []},
                    });
                    return results;
                }
                target.addToLinksRelationship(imageId);
                const promisesArr = [];
                if (target.type === "OdmOsUpgradePlan") {
                    const planOnlyLinkIds = target.planOnlyLinkIds || [];
                    if (!planOnlyLinkIds.includes(imageId)) {
                        planOnlyLinkIds.push(imageId);
                        target.setPlanOnlyLinkIds(planOnlyLinkIds);
                        promisesArr.push(target.update());
                    } else {
                        promisesArr.push(target.updateRelationships());
                    }
                } else {
                    promisesArr.push(target.updateRelationships());
                }

                return Promise.all(promisesArr)
                    .then(() => {
                        results.push({
                            status: "fulfilled",
                            value: {success: 1, skipped: 0, failed: 0, errors: []},
                        });
                        return results;
                    })
                    .catch((err) => {
                        target.clearPendingRelationships();
                        if (target.type === "OdmOsUpgradePlan") {
                            target.clearPendingPatches();
                        }
                        this._logger.warn(
                            {error: err, imageId},
                            "Failed to update target relationships"
                        );
                        results.push({
                            status: "fulfilled",
                            value: {
                                success: 0,
                                skipped: 0,
                                failed: 1,
                                errors: [
                                    err.errorMsg || err.message || "Failed to link image to target",
                                ],
                            },
                        });
                        return results;
                    });
            });
        }, Promise.resolve([]));
    }

    /**
     * Links images to target entities - generic method for both editor and explorer modes.
     * @private
     */
    async _linkImagesToTargets(osLinkIds, targets) {
        const targetEntities = Array.isArray(targets) ? targets : [];

        if (targetEntities.length === 0) {
            this._logger.warn("AddImagesComponent._linkImagesToTargets() - No targets to link");
            return;
        }

        // Process each target entity (link all images sequentially per target)
        const targetPromises = targetEntities.map((target) => {
            return this._getExistingLinkIds(target).then((existingLinkIds) => {
                return this._linkImagesToSingleTarget(target, osLinkIds, existingLinkIds);
            });
        });

        // Wait for all targets to complete
        const targetResults = await Promise.allSettled(targetPromises);

        // Aggregate all results from all targets
        targetResults.forEach((targetResult) => {
            if (targetResult.status === "fulfilled") {
                // Each target returns an array of image results
                this._aggregateResults(targetResult.value);
            } else {
                // Target promise rejected unexpectedly
                this._logger.warn(
                    {error: targetResult.reason},
                    "Target processing failed unexpectedly"
                );
                osLinkIds.forEach(() => {
                    this._summaryValues.total += 1;
                    this._summaryValues.failureCount += 1;
                });
                this._summaryValues.errors.push(
                    targetResult.reason?.message || "Target processing failed"
                );
            }
        });
    }

    /**
     * Aggregates results from image linking operations.
     * @private
     */
    _aggregateResults(results) {
        results.forEach((result) => {
            if (result.status === "fulfilled") {
                this._summaryValues.total +=
                    result.value.success + result.value.failed + (result.value.skipped || 0);
                this._summaryValues.successCount += result.value.success;
                this._summaryValues.failureCount += result.value.failed;
                this._summaryValues.skippedCount += result.value.skipped || 0;
                this._summaryValues.errors.push(...result.value.errors);
            } else {
                // Promise rejected unexpectedly
                this._summaryValues.total += 1;
                this._summaryValues.failureCount += 1;
                this._summaryValues.errors.push(result.reason?.message || "Unexpected error");
            }
        });
    }

    /**
     * Links images to all selected targets.
     * Loads all target entities upfront and passes them to _linkImagesToTargets.
     * @private
     */
    async _linkImageToTargets(osLinkIds) {
        let targetEntities = [];
        // Load all target entities in one call
        const targetIds = Array.isArray(this._selectedTargets)
            ? this._selectedTargets
            : [this._selectedTargets.id];

        if (targetIds.length === 0) {
            this._logger.warn("AddImagesComponent._linkImageToTargets() - No targets selected");
            return;
        }

        // Load all targets
        targetEntities = await this._targetEntity.loadByQuery(
            this._activeOrgId,
            this._targetEntity.getEntityType(),
            {id: {$in: targetIds}}
        );

        if (targetEntities.length === 0) {
            this._logger.warn(
                "AddImagesComponent._linkImageToTargets() - All targets failed to load"
            );
            // Mark all as failed
            const totalAttempts = targetIds.length * osLinkIds.length;
            this._summaryValues.total = totalAttempts;
            this._summaryValues.failureCount = totalAttempts;
            this._summaryValues.errors.push("All targets failed to load");
            return;
        }

        // Pass loaded entities to generic linking method
        return this._linkImagesToTargets(osLinkIds, targetEntities);
    }

    /**
     * Generates OS link (image) options for the combobox.
     * @private
     */
    _generateOsLinkOptions() {
        const links = this._odmOsLinks;
        return links
            .map((link) => ({
                label: link.name,
                value: link.id,
            }))
            .filter((opt) => {
                if (opt.label) {
                    return true;
                }
                this._logger.warn(
                    {id: opt.value},
                    "Name not available for the image. Dropping from options list"
                );
                return false;
            });
    }

    _getExistingLinks(target) {
        return target.loadRelatedLinks({}, {id: true, name: true});
    }

    _getExistingLinkIds(target, existingLinks, linksLoaded = false) {
        if (linksLoaded) {
            return Promise.resolve(existingLinks.map((link) => link.id));
        }
        return this._getExistingLinks(target).then((links) => links.map((link) => link.id));
    }

    _createNewImageEntity(name, description, filename) {
        const newOdmOsLink = new this._OdmOsLink(this._activeOrgId);
        newOdmOsLink.name = name;
        newOdmOsLink.description = description;
        newOdmOsLink.linkStatus = "UPLOAD";
        newOdmOsLink.url = filename;
        newOdmOsLink.manual = true;
        return newOdmOsLink.create();
    }
}

module.exports = AddImagesComponent;
